import React, {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import classnames from 'classnames'
import {
  validateDOMAttributes,
  getStatusState,
  combineDescribedBy,
  extendPropsWithContext,
} from '../../shared/component-helper'
import { pickFormElementProps } from '../../shared/helpers/filterValidProps'
import AlignmentHelper from '../../shared/AlignmentHelper'
import { createSpacingClasses } from '../space/SpacingHelper'
import {
  skeletonDOMAttributes,
  createSkeletonClass,
} from '../skeleton/SkeletonHelper'

import Context from '../../shared/Context'
import Suffix from '../../shared/helpers/Suffix'
import FormLabel from '../form-label/FormLabel'
import FormStatus, {
  FormStatusState,
  FormStatusText,
} from '../form-status/FormStatus'
import useId from '../../shared/helpers/useId'
import { GlobalStatusConfigObject } from '../GlobalStatus'
import { SkeletonShow } from '../Skeleton'
import { SpacingProps } from '../space/types'

export type SwitchLabelPosition = 'left' | 'right'
export type SwitchSize = 'default' | 'medium' | 'large'
export type SwitchAttributes = string | Record<string, unknown>
export type SwitchOnChangeParams = {
  checked: boolean
  event: MouseEvent | TouchEvent | KeyboardEvent
}
export type SwitchOnClickParams = React.MouseEvent<HTMLInputElement> & {
  checked: boolean
  event: React.MouseEvent<HTMLInputElement>
}
export type SwitchOnChange = (args: SwitchOnChangeParams) => void

export type SwitchProps = {
  /**
   * Use either the `label` property or provide a custom one.
   */
  label?: React.ReactNode
  /**
   * Defines the position of the `label`. Use either `left` or `right`. Defaults to `right`.
   */
  labelPosition?: SwitchLabelPosition
  /**
   * Use `true` to make the label only readable by screen readers.
   */
  labelSrOnly?: boolean
  /**
   * <em>(required)</em> the `title` of the input - describing it a bit further for accessibility reasons.
   */
  title?: string
  /**
   * Determine whether the switch is checked or not. The default will be `false`.
   */
  checked?: boolean
  disabled?: boolean
  id?: string
  /**
   * The size of the switch. For now there is "medium" (default) and "large".
   */
  size?: SwitchSize
  /**
   * Text with a status message. The style defaults to an error message. You can use `true` to only get the status color, without a message.
   */
  status?: FormStatusText
  /**
   * Defines the state of the status. Currently, there are two statuses `[error, info]`. Defaults to `error`.
   */
  statusState?: FormStatusState
  /**
   * Use an object to define additional FormStatus properties.
   */
  statusProps?: Record<string, unknown>
  /**
   * The <a href="/uilib/components/global-status/properties/#configuration-object">configuration</a> used for the target <a href="/uilib/components/global-status">GlobalStatus</a>.
   */
  globalStatus?: GlobalStatusConfigObject
  statusNoAnimation?: boolean
  /**
   * Text describing the content of the Switch more than the label. You can also send in a React component, so it gets wrapped inside the Switch component.
   */
  suffix?: React.ReactNode
  value?: string
  attributes?: SwitchAttributes
  readOnly?: boolean
  /**
   * If set to `true`, an overlaying skeleton with animation will be shown.
   */
  skeleton?: SkeletonShow
  className?: string
  children?: React.ReactNode
  /**
   * Will be called on state changes made by the user. Returns a boolean `{ checked, event }`.
   */
  onChange?: SwitchOnChange
  /**
   * Will be called on state changes made by the user, but with a delay. This way the user sees the animation before e.g. an error will be removed. Returns a boolean `{ checked, event }`.
   */
  /**
   * Will be called on click made by the user. Returns the ClickEvent.
   */
  onClick?: (args: SwitchOnClickParams) => void
  onChangeEnd?: SwitchOnChange
  onStateUpdate?: SwitchOnChange
  /**
   * By providing a React.ref we can get the internally used input element (DOM). E.g. `innerRef={myRef}` by using `React.createRef()` or `React.useRef()`.
   */
  innerRef?:
    | React.MutableRefObject<HTMLInputElement>
    | ((elem: HTMLInputElement) => void)
} & Omit<
  React.HTMLProps<HTMLElement>,
  'ref' | 'size' | 'onChange' | 'onClick' | 'innerRef' | 'label'
> &
  SpacingProps

const defaultProps = {
  statusState: 'error',
}

export default function Switch(props: SwitchProps) {
  const context = useContext(Context)

  const allProps = extractPropsFromContext()

  const {
    value,
    size,
    status,
    statusState,
    statusProps,
    globalStatus,
    statusNoAnimation,
    suffix,
    label,
    labelPosition,
    labelSrOnly,
    title,
    disabled,
    readOnly,
    skeleton,
    className,
    id: idProp,
    checked: checkedProp,
    onChange,
    onChangeEnd,
    onClick,
    innerRef: innerRefProp,
    ...rest
  } = allProps

  const [, forceUpdate] = useReducer(() => ({}), {})
  const id = useId(idProp)
  const isFn = typeof innerRefProp === 'function'
  const refHook = useRef<HTMLInputElement>()
  const innerRef = (!isFn && innerRefProp) || refHook

  const preventChangeRef = useRef(false)
  const isCheckedRef = useRef(checkedProp ?? false)
  const prevCheckedRef = useRef(checkedProp)

  useEffect(() => {
    if (isFn) {
      innerRefProp?.(refHook.current)
    }
  }, [innerRefProp, isFn, refHook])

  useEffect(() => {
    if (checkedProp !== prevCheckedRef.current) {
      isCheckedRef.current = !!checkedProp
      prevCheckedRef.current = !!checkedProp
      forceUpdate()
    }
  }, [checkedProp])

  const callOnChange = useCallback(
    ({ checked, event }) => {
      onChange?.({ checked, event })
    },
    [onChange]
  )

  const onChangeHandler = useCallback(
    (event) => {
      if (preventChangeRef.current) {
        return // stop here
      }

      const updatedChecked = !isCheckedRef.current

      isCheckedRef.current = updatedChecked
      forceUpdate()
      callOnChange({ checked: updatedChecked, event })

      if (onChangeEnd) {
        if (event && event.persist) {
          event.persist()
        }
        setTimeout(
          () => onChangeEnd({ checked: updatedChecked, event }),
          500
        )
      }

      // help firefox and safari to have an correct state after a click
      if (innerRef.current) {
        innerRef.current.focus()
      }
    },
    [callOnChange, innerRef, onChangeEnd]
  )

  const onClickHandler: React.MouseEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        const preventDefault = () => {
          event.preventDefault()

          if (event.target['checked'] !== isCheckedRef.current) {
            preventChangeRef.current = true
            isCheckedRef.current = !isCheckedRef.current
            forceUpdate()
          }
        }

        if (readOnly) {
          return preventDefault()
        }

        onClick?.({
          checked: isCheckedRef.current,
          preventDefault,
          event,
          ...event,
        })
      },
      [onClick, readOnly]
    )

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Enter':
          onChangeHandler(event)
          break
      }
    },
    [onChangeHandler]
  )

  const showStatus = useMemo(() => getStatusState(status), [status])

  const mainParams = {
    className: classnames(
      'dnb-switch',
      size && `dnb-switch--${size}`,
      status && `dnb-switch__status--${statusState}`,
      `dnb-switch--label-position-${labelPosition || 'right'}`,
      'dnb-form-component',
      createSkeletonClass(null, skeleton),
      createSpacingClasses(props),
      className
    ),
  }

  const inputParams = {
    disabled,
    checked: isCheckedRef.current,
    ...rest,
  }

  if (showStatus || suffix) {
    inputParams['aria-describedby'] = combineDescribedBy(
      inputParams,
      showStatus ? id + '-status' : null,
      suffix ? id + '-suffix' : null
    )
  }
  if (readOnly) {
    inputParams['aria-readonly'] = readOnly
  }

  skeletonDOMAttributes(inputParams, skeleton, context)
  validateDOMAttributes(props, inputParams)

  const helperParams = useMemo(
    () => ({
      onMouseDown: (e: React.MouseEvent<HTMLSpanElement>) =>
        e.preventDefault(),
    }),
    []
  )

  const labelComp = useMemo(
    () =>
      label && (
        <FormLabel
          id={id + '-label'}
          forId={id}
          text={label}
          disabled={disabled}
          skeleton={skeleton}
          srOnly={labelSrOnly}
        />
      ),
    [disabled, id, label, labelSrOnly, skeleton]
  )

  return (
    <span {...mainParams}>
      <span className="dnb-switch__order">
        {labelPosition === 'left' && labelComp}

        <span className="dnb-switch__inner">
          <AlignmentHelper />

          <FormStatus
            show={showStatus}
            id={id + '-form-status'}
            globalStatus={globalStatus}
            label={label}
            widthSelector={id + ', ' + id + '-label'}
            text={status}
            state={statusState}
            skeleton={skeleton}
            noAnimation={statusNoAnimation}
            {...statusProps}
          />

          <span className="dnb-switch__shell">
            {(labelPosition === 'right' || !labelPosition) && labelComp}

            <span className="dnb-switch__row">
              <input
                id={id}
                name={id}
                type="checkbox"
                role="switch"
                title={title}
                aria-checked={isCheckedRef.current}
                className="dnb-switch__input"
                value={isCheckedRef.current ? value || '' : ''}
                ref={innerRef}
                {...inputParams}
                onChange={onChangeHandler}
                onClick={onClickHandler}
                onKeyDown={onKeyDownHandler}
              />
              <span
                draggable
                aria-hidden
                className="dnb-switch__background"
                onDragStart={onChangeHandler}
                {...helperParams}
              />
              <span
                className={classnames(
                  'dnb-switch__button',
                  createSkeletonClass('shape', skeleton, context)
                )}
                aria-hidden
              >
                <span className="dnb-switch__focus">
                  <span className="dnb-switch__focus__inner" />
                </span>
              </span>
            </span>

            {suffix && (
              <Suffix
                className="dnb-switch__suffix"
                id={id + '-suffix'} // used for "aria-describedby"
                context={props}
              >
                {suffix}
              </Suffix>
            )}
          </span>
        </span>
      </span>
    </span>
  )

  function extractPropsFromContext() {
    return extendPropsWithContext(
      props,
      defaultProps,
      { skeleton: context?.skeleton },
      // Deprecated – can be removed in v11
      pickFormElementProps(context?.FormRow),
      pickFormElementProps(context?.formElement),
      context.Switch
    )
  }
}
